// Multi-value creator picker with a throttled name search: chips of the selected creators, an input
// that searches `vendor {creators}` by name / slug for the given role(s).
import React, { useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline"
import { ListCreators, CreateCreator } from "../../../AdminShared/api"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

const nameOf = (creator, language) => creator?.names?.[language] || creator?.names?.en || creator?.name || ""
// `vendor {creators}` answers the photo as a url (a srcset list for a
// responsive image): the first source is enough at this size
const photoOf = (creator) => (Array.isArray(creator?.image) ? creator.image[0] : creator?.image) || null

/** The creator's photo, or their initial when they have none. */
const Avatar = ({ creator, name, className = "" }) => {
  const photo = photoOf(creator)
  if (photo) return <img src={photo} alt="" loading="lazy" className={`rounded-full object-cover shrink-0 bg-admin_neutral_1 ${className}`} />
  return (
    <span className={`rounded-full shrink-0 bg-admin_neutral_1 text-admin_text_secondary inline-flex items-center justify-center text-[10px] font-semibold uppercase ${className}`}>
      {String(name || "?").trim().charAt(0)}
    </span>
  )
}

export default function SelectCreator({ label, role, language = "en", value = [], setValue, disabled = false, "data-testid": testId }) {
  const t = useProductEditorTexts()
  const [inputValue, setInputValue] = useState("")
  const [options, setOptions] = useState([])
  const [known, setKnown] = useState({})
  const [open, setOpen] = useState(false)
  const timer = useRef(null)
  const box = useRef(null)

  const ids = useMemo(() => (value || []).map((v) => (v && typeof v === "object" ? String(v._id) : String(v))), [value])

  // resolve the names of the assigned creators once
  useEffect(() => {
    const missing = ids.filter((id) => !known[id])
    if (!missing.length) return
    ListCreators(undefined, undefined, missing).then((res) => {
      const found = Object.fromEntries((res?.list || []).map((c) => [c._id, c]))
      setKnown((k) => ({ ...k, ...found }))
    }).catch(() => {})
  }, [ids.join(",")])

  useEffect(() => {
    clearTimeout(timer.current)
    if (!inputValue.trim()) {
      setOptions([])
      return
    }
    timer.current = setTimeout(() => {
      ListCreators(inputValue.trim(), role).then((res) => {
        const list = (res?.list || []).filter((c) => !ids.includes(c._id))
        setOptions(list)
        setKnown((k) => ({ ...k, ...Object.fromEntries(list.map((c) => [c._id, c])) }))
      }).catch(() => setOptions([]))
    }, 300)
    return () => clearTimeout(timer.current)
  }, [inputValue, role, ids.join(",")])

  useEffect(() => {
    const onDown = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [])

  const add = (creator) => {
    setValue([...ids, creator._id])
    setInputValue("")
    setOptions([])
    setOpen(false)
  }
  const remove = (id) => setValue(ids.filter((x) => x !== id))

  // a name nobody has yet (a new book's author): added as typed, made a creator of this role
  const typed = inputValue.replace(/\s+/g, " ").trim()
  const exact = options.some((c) => Object.values(c.names || {}).concat(c.name).some((n) => String(n || "").trim().toLowerCase() === typed.toLowerCase()))
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState("")
  const createNew = async () => {
    if (!typed || creating) return
    setCreating(true)
    setCreateError("")
    const res = await CreateCreator(Array.isArray(role) ? role[0] : role, typed).catch(() => null)
    setCreating(false)
    if (!res?.creator) return setCreateError(res?.error === "name_invalid" ? t`Enter a name in letters only` : t`The name could not be added`)
    setKnown((k) => ({ ...k, [res.creator._id]: res.creator }))
    add(res.creator)
  }

  return (
    <div ref={box} className="relative mb-2" data-testid={testId}>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-admin_text_secondary">{label}</span>
        <div className={twMerge("min-h-[38px] flex flex-wrap items-center gap-1 px-2 py-1 rounded-md border border-admin_bordered bg-admin_paper focus-within:border-admin_primary", disabled ? "opacity-60" : "")}>
          {ids.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 h-7 pl-1 pr-1 rounded-full bg-admin_primary/10 text-admin_primary text-sm" data-testid="creator-chip">
              <Avatar creator={known[id]} name={nameOf(known[id], language)} className="w-5 h-5" />
              {nameOf(known[id], language) || id}
              {!disabled && (
                <button type="button" onClick={() => remove(id)} className="w-5 h-5 rounded-full hover:bg-admin_primary/20 flex items-center justify-center" aria-label={t("remove %s", nameOf(known[id], language) || id)}>
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          <input
            value={inputValue}
            disabled={disabled}
            onChange={(e) => {
              setInputValue(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            placeholder={ids.length ? "" : t`Start typing a name`}
            className="flex-1 min-w-[160px] h-7 px-1 bg-transparent outline-none text-sm text-admin_text"
          />
        </div>
      </label>
      {open && inputValue.trim() && (
        <ul className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-admin_paper border border-admin_bordered rounded-md shadow-lg py-1" role="listbox">
          {!options.length && <li className="px-3 py-2 text-sm text-admin_text_secondary">{t`No creators match`}</li>}
          {typed.length >= 2 && !exact && (
            <li role="option" onClick={createNew} className="px-3 py-2 text-sm cursor-pointer hover:bg-admin_neutral_1 flex items-center gap-2 text-admin_primary font-medium" data-testid="creator-create">
              <PlusIcon className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{creating ? t`Adding…` : t("Add “%s” as a new name", typed)}</span>
            </li>
          )}
          {createError && <li className="px-3 py-2 text-xs text-admin_error">{createError}</li>}
          {options.map((c) => (
            <li key={c._id} role="option" onClick={() => add(c)} className="px-3 py-2 text-sm cursor-pointer hover:bg-admin_neutral_1 flex items-center gap-2">
              <Avatar creator={c} name={nameOf(c, language)} className="w-7 h-7" />
              <span className="flex-1 truncate">{nameOf(c, language)}</span>
              <span className="text-xs text-admin_text_secondary">{t(c.role)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
